'use client'

import { Fragment } from 'react'
import { FormattedDate, useIntl } from 'react-intl'
import { Cell, Text, Icon, ProgressIndicator, ProgressItemVertical } from '@vinted/web-ui'
import classNames from 'classnames'
import {
  CheckCircle24,
  CheckCircleFilled24,
  Redirect24,
  StatusActive24,
} from '@vinted/monochrome-icons'

import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  clickEvent,
  TrackingJourneySummaryDetails,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'

type Props = {
  transactionId?: number
  trackingDetails: Array<TrackingJourneySummaryDetails>
}

const Timeline = ({ transactionId, trackingDetails }: Props) => {
  const { track } = useTracking()
  const translate = useTranslate('conversation.tracking_journey_details')
  const { formatDate } = useIntl()

  const isProgressComponentAbTestEnabled =
    useAbTest('dd_tracking_screen_progress_component_v3')?.variant === 'on'

  function trackProviderTrackingUrlClick(carrierId: number, carrierPriority: number) {
    const targetDetails = {
      transaction_id: transactionId,
      carrier_id: carrierId,
      carrier_priority: carrierPriority,
    }

    return () => {
      track(
        clickEvent({
          target: 'open_tracking_page',
          screen: 'track_shipment_view',
          targetDetails: JSON.stringify(targetDetails),
        }),
      )
    }
  }

  function renderContent(trackingDetail: TrackingJourneySummaryDetails, isFirstElement: boolean) {
    const textType = isFirstElement ? Text.Type.Title : Text.Type.Body

    return (
      <div className="timeline__content">
        <Cell
          testId={`timeline-content-${trackingDetail.timestamp}`}
          styling={Cell.Styling.Tight}
          theme="transparent"
          title={
            <Text
              as={isFirstElement ? 'h2' : 'span'}
              text={trackingDetail.message}
              type={textType}
              theme="amplified"
            />
          }
          body={
            <Text
              as="span"
              text={
                <FormattedDate
                  value={trackingDetail.timestamp}
                  year="numeric"
                  month="long"
                  day="numeric"
                  hour="numeric"
                  minute="numeric"
                />
              }
            />
          }
          suffix={
            trackingDetail.carrier?.trackingUrl && (
              <a
                href={trackingDetail.carrier.trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="carrier_url"
                title={translate('a11y.link_title')}
                onClick={trackProviderTrackingUrlClick(
                  trackingDetail.carrier.id,
                  trackingDetail.carrier.priority,
                )}
              >
                <Icon
                  name={Redirect24}
                  color={Icon.Color.GreyscaleLevel2}
                  testId="timeline-redirect-icon"
                />
              </a>
            )
          }
        />
      </div>
    )
  }

  function renderIcon(trackingDetail: TrackingJourneySummaryDetails, isFirstElement: boolean) {
    const cellType = trackingDetail.cellType === 'finished'
    const isCurrentIcon = isFirstElement ? StatusActive24 : CheckCircle24

    return <Icon name={cellType ? CheckCircleFilled24 : isCurrentIcon} color={Icon.Color.Primary} />
  }

  const renderProgressIndicator = () => (
    <ProgressIndicator testId="tracking-progress-indicator">
      {trackingDetails.map((trackingDetail, index) => {
        const isFirstElement = index === 0

        return (
          <ProgressItemVertical
            key={trackingDetail.timestamp}
            active={isFirstElement}
            caption={formatDate(trackingDetail.timestamp, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
            })}
            progressionState="completed"
            title={trackingDetail.message}
            showCaption
            suffix={
              trackingDetail.carrier?.trackingUrl && (
                <a
                  href={trackingDetail.carrier.trackingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="carrier_url"
                  title={translate('a11y.link_title')}
                  onClick={trackProviderTrackingUrlClick(
                    trackingDetail.carrier.id,
                    trackingDetail.carrier.priority,
                  )}
                >
                  <Icon
                    name={Redirect24}
                    color={Icon.Color.GreyscaleLevel2}
                    testId="tracking-progress-indicator-redirect-icon"
                  />
                </a>
              )
            }
          />
        )
      })}
    </ProgressIndicator>
  )

  const renderTimeline = () => (
    <div className="timeline__wrapper">
      {trackingDetails.map((trackingDetail, index) => {
        const isFirstElement = index === 0
        const isLastElement = trackingDetails.length === index + 1

        return (
          <Fragment key={trackingDetail.timestamp}>
            <div
              className={classNames(
                'timeline__item',
                isFirstElement ? 'timeline__item--active' : null,
              )}
            >
              <div className="timeline__mark">
                {renderIcon(trackingDetail, isFirstElement)}
                {!isLastElement && <div className="timeline__separator" />}
              </div>
              {renderContent(trackingDetail, isFirstElement)}
            </div>
            {!isLastElement && (
              <div className="timeline__connector">
                <div className="timeline__connector-line" />
              </div>
            )}
          </Fragment>
        )
      })}
    </div>
  )

  return isProgressComponentAbTestEnabled ? renderProgressIndicator() : renderTimeline()
}

export default Timeline
